<template>
  <div>
    <mia-panel title="Kostenstellen verwalten">
      <template #default>
        <mia-wait for="fetching booking option cost centers">
          <template #waiting>
            <div class="flex justify-center py-8">
              <mia-wave-loader />
            </div>
          </template>

          <div class="flex p-4 space-x-3">
            <div class="flex-grow">
              <input
                v-model="filters.query"
                type="text"
                class="form-input"
                placeholder="Kostenstellen durchsuchen"
              >
            </div>
            <div>
              <mia-submit-button
                label="Kostenstelle anlegen"
                size="small"
                :disabled="isAddingCostCenter"
                :class="{ 'opacity-50': isAddingCostCenter }"
                @click="addCostCenter"
              />
            </div>
          </div>

          <div
            v-if="isAddingCostCenter"
            class="flex items-end p-4 space-x-3"
          >
            <div class="flex-grow">
              <label
                for="addCostCenterTitle"
                class="tracking-wider text-gray-800"
              >
                Titel
              </label>

              <input
                id="addCostCenterTitle"
                ref="addCostCenterTitleInput"
                v-model="addingCostCenter.item_title"
                placeholder="Titel der Kostenstelle"
                type="text"
                class="mt-1 form-input"
                @keydown.enter="storeCostCenter"
              >
            </div>
            <div class="flex space-x-3">
              <mia-submit-button
                label="Speichern"
                size="small"
                :disabled="addingCostCenter.item_title.trim().length === 0"
                :class="{ 'opacity-50': addingCostCenter.item_title.trim().length === 0 }"
                @click="storeCostCenter"
              />
              <mia-cancel-button
                v-if="addingCostCenter !== null"
                label="Abbrechen"
                size="small"
                @click="addingCostCenter = null"
              />
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th
                  class="sticky w-full p-4 bg-gray-400"
                  style="top: -10px;"
                >
                  Titel
                </th>
                <th
                  class="sticky p-4 bg-gray-400"
                  style="top: -10px;"
                >
                  Verwendet
                </th>
                <th
                  class="sticky p-4 bg-gray-400"
                  style="top: -10px;"
                >
                  Aktion
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(costCenter, index) in filteredCostCenters"
                :key="costCenter.id"
              >
                <td
                  :class="{ 'bg-gray-200': index === 0 || index % 2 === 0 }"
                  class="p-4"
                >
                  <input
                    v-if="isCostCenterEditing(costCenter)"
                    v-model="editingCostCenter.item_title"
                    type="text"
                    class="form-input"
                    :data-id="costCenter.id"
                    @keydown.enter="updateCostCenter(editingCostCenter)"
                  >
                  <span v-else>
                    {{ costCenter.item_title }}
                  </span>
                </td>
                <td
                  :class="{ 'bg-gray-200': index === 0 || index % 2 === 0 }"
                  class="p-4 text-center whitespace-no-wrap"
                >
                  {{ costCenter.item_usage_count }}
                </td>
                <td
                  :class="{ 'bg-gray-200': index === 0 || index % 2 === 0 }"
                  class="p-4 text-center whitespace-no-wrap"
                >
                  <div
                    v-if="costCenter.item_editable === 'Y'"
                    class="flex justify-end space-x-3"
                  >
                    <button
                      v-if="!isCostCenterEditing(costCenter)"
                      type="button"
                      class="text-orange-mia hover:text-orange-mia-dark hover:underline"
                      title="Kostenstelle bearbeiten"
                      @click="edit(costCenter)"
                    >
                      Bearbeiten
                    </button>
                    <button
                      v-else
                      type="button"
                      class="text-orange-mia hover:text-orange-mia-dark hover:underline"
                      title="Änderungen speichern"
                      @click="updateCostCenter(editingCostCenter)"
                    >
                      Speichern
                    </button>

                    <button
                      v-if="!isCostCenterEditing(costCenter)"
                      type="button"
                      class="text-orange-mia hover:text-orange-mia-dark hover:underline"
                      title="Kostenstelle löschen"
                      @click="removeCostCenter(costCenter)"
                    >
                      Löschen
                    </button>

                    <button
                      v-if="isCostCenterEditing(costCenter)"
                      type="button"
                      class="text-orange-mia hover:text-orange-mia-dark hover:underline"
                      title="Abbrechen"
                      @click="editingCostCenter = null"
                    >
                      Abbrechen
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </mia-wait>
      </template>
    </mia-panel>

    <mia-modal
      v-if="deletingCostCenter !== null"
      id="delete-bookingoption-cost-center-confirmation-modal"
      classes="v--modal flex flex-col"
      width="60%"
      height="auto"
      :show="deletingCostCenter !== null"
      @closed="deletingCostCenter = null"
    >
      <mia-modal-header>
        <h3 class="py-0 my-0">
          Kostenstelle löschen
        </h3>
      </mia-modal-header>

      <div class="p-4">
        <p>
          Die Kostenstelle <b>{{ deletingCostCenter.item_title }}</b> ist mehreren Buchungsoptionen zugeordnet.<br>Bitte wählen Sie die Kostenstelle aus, zu der die Zuordnungen migriert werden sollen.
        </p>

        <p
          v-if="migratableCostCenters.length === 0"
          class="font-bold"
        >
          Bitte legen Sie zuerst eine weitere Kostenstelle an, damit die Daten migriert werden können!
        </p>
        <mia-select
          v-else
          v-model="migrationCostCenterId"
          class="w-full"
        >
          <option
            value=""
            disabled
          >
            Neue Kostenstelle auswählen
          </option>
          <option
            v-for="costCenter in migratableCostCenters"
            :key="costCenter.id"
            :value="costCenter.id"
          >
            {{ costCenter.item_title }}
          </option>
        </mia-select>
      </div>

      <mia-modal-footer class="flex items-center justify-end space-x-3">
        <mia-cancel-button
          label="Abbrechen"
          type="button"
          @click="deletingCostCenter = null; migrationCostCenterId = '';"
        />

        <mia-submit-button
          label="Kostenstelle löschen"
          type="button"
          :disabled="migrationCostCenterId === ''"
          :is-loading="$wait.is('deleting cost center')"
          :class="{ 'opacity-50': migrationCostCenterId === '' }"
          @click="deleteCostCenter(deletingCostCenter, migrationCostCenterId)"
        />
      </mia-modal-footer>
    </mia-modal>
  </div>
</template>

<script>
import sortBy from 'lodash/sortBy'
import {
  deleteCostCenter, getCostCenters, storeCostCenter, updateCostCenter
} from '@/api/bookingOptions'
import CancelButton from '@/components/cars/edit/CancelButton.vue'
import SubmitButton from '@/components/cars/edit/SubmitButton.vue'
import Panel from '@/components/ui/widgets/Panel.vue'
import { tryFocus, trySelect } from '@/helpers/utils'

export default {
  components: {
    'mia-cancel-button': CancelButton,
    'mia-panel': Panel,
    'mia-submit-button': SubmitButton
  },

  data: () => ({
    costCenters: [],
    addingCostCenter: null,
    editingCostCenter: null,
    deletingCostCenter: null,
    migrationCostCenterId: '',
    filters: {
      query: ''
    }
  }),

  computed: {
    isAddingCostCenter() {
      return this.addingCostCenter !== null
    },

    sortedCostCenters() {
      return sortBy(this.costCenters, ['item_title'])
    },

    filteredCostCenters() {
      let costCenters = this.sortedCostCenters

      if (this.filters.query.length > 0) {
        costCenters = costCenters.filter(costCenter => costCenter.item_title.toLowerCase().includes(this.filters.query.toLowerCase()))
      }

      return costCenters
    },

    migratableCostCenters() {
      return this.sortedCostCenters.filter(costCenter => costCenter.id !== this.deletingCostCenter.id)
    }
  },

  created() {
    window.eventBus.$emit('mia-set-breadcrumb', [
      {
        link: {
          path: '/system'
        },
        text: 'System'
      },
      {
        link: {
          path: '/system/booking-options'
        },
        text: 'Buchungsoptionen'
      },
      {
        link: {
          path: '/system/booking-options/cost-centers'
        },
        text: 'Kostenstellen verwalten'
      }
    ])

    this.fetchCostCenters()
  },

  methods: {
    async fetchCostCenters() {
      this.$wait.start('fetching booking option cost centers')
      const [error, costCenters] = await getCostCenters()
      this.$wait.end('fetching booking option cost centers')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Kostenstellen konnten nicht geladen werden.']
        })

        return
      }

      this.costCenters = costCenters
    },

    addCostCenter() {
      this.addingCostCenter = {
        id: Math.random(),
        item_title: ''
      }

      this.$nextTick(() => {
        tryFocus(this.$refs.addCostCenterTitleInput)
      })
    },

    async storeCostCenter() {
      const [errors, newCostCenter] = await storeCostCenter(this.addingCostCenter)

      if (errors) {
        window.eventBus.$emit('mia-error', {
          error: ['Kostenstelle konnte nicht angelegt werden.']
        })

        return
      }

      this.costCenters.push(newCostCenter)
      this.addingCostCenter = null

      window.eventBus.$emit('mia-info', {
        info: ['Kostenstelle wurde erfolgreich angelegt.']
      })
    },

    async updateCostCenter(costCenter) {
      const [error, updatedCostCenter] = await updateCostCenter(costCenter.id, costCenter)

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Kostenstelle konnte nicht aktualisiert werden.']
        })
        return
      }

      this.costCenters = this.costCenters.map(cc => (cc.id === costCenter.id ? updatedCostCenter : cc))
      this.editingCostCenter = null

      window.eventBus.$emit('mia-info', {
        info: ['Kostenstelle erfolgreich aktualisiert.']
      })
    },

    edit(costCenter) {
      this.editingCostCenter = { ...costCenter }

      this.$nextTick(() => {
        const input = this.$el.querySelector(`input[data-id="${costCenter.id}"]`)

        tryFocus(input)
        trySelect(input)
      })
    },

    removeCostCenter(costCenter) {
      if (costCenter.item_usage_count === 0) {
        this.deleteCostCenter(costCenter)
        return
      }

      this.deletingCostCenter = costCenter
    },

    async deleteCostCenter(costCenter, migrationCostCenterId = null) {
      this.$wait.start('deleting cost center')
      const [error] = await deleteCostCenter(costCenter.id, migrationCostCenterId)
      this.$wait.end('deleting cost center')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Kostenstelle konnte nicht gelöscht werden.']
        })
        return
      }

      this.fetchCostCenters()
      this.deletingCostCenter = null
      this.migrationCostCenterId = ''

      window.eventBus.$emit('mia-info', {
        info: ['Kostenstelle erfolgreich gelöscht.']
      })
    },

    isCostCenterEditing(costCenter) {
      return this.editingCostCenter?.id === costCenter.id
    }
  }
}
</script>
